import { useState } from 'react';
import { isEncryptionAvailable } from '../../utils/localCredStore';
import { isNativePlayerAvailable } from '../../utils/localRtspBridge';

// crypto.subtle (needed to encrypt saved credentials) requires a secure
// context — unavailable over plain http://<lan-ip>, which is how Local
// Mode is typically reached. Detected once at load; doesn't change mid-session.
const CAN_REMEMBER = isEncryptionAvailable();
const NATIVE_PLAYER = isNativePlayerAvailable();

export const emptyLocalCameraForm = { name: '', protocol: 'rtsp', host: '', port: '554', path: '', transport: 'tcp', username: '', password: '', remember: CAN_REMEMBER };

// Cameras synced from the account on login (source: 'server-sync', see
// syncServerCamerasToLocal in localCredStore.js) plus any added manually here
// — the manual flow is unchanged so a device that's never logged in still
// works exactly as before. Viewing is handed off to the Android app's native
// RTSP player (see localRtspBridge.js) — there is no backend stream to
// connect/disconnect from here.
export default function LocalCamerasTab({
  cameras, form, setForm, showPw, setShowPw, error,
  promptFor, setPromptFor,
  handleAddSubmit, handleView, handleInlineConnect, handleForget, handleClearAll,
}) {
  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <div className="lm-notice">
        ⚠️ Cameras on this tab are viewed straight from the network, without signing in to your account —
        anyone with access to this device can view the cameras saved here. Credentials you choose to
        remember are encrypted before being stored on this device, but that protection only holds against
        someone reading the raw storage — not against another script running on this page.
      </div>

      {!NATIVE_PLAYER && (
        <div className="lm-notice">
          📱 Live camera view needs the Parvekshak Android app — open this page there to view streams.
          You can still add and manage cameras here.
        </div>
      )}

      <div className="lm-panel">
        <h2>Add a camera</h2>
        <form className="lm-form" onSubmit={handleAddSubmit}>
          <div className="lm-field">
            <label>Name (optional)</label>
            <input value={form.name} onChange={setField('name')} placeholder="e.g. Front Gate" />
          </div>
          <div className="lm-row">
            <div className="lm-field lm-field-sm">
              <label>Protocol</label>
              <select value={form.protocol} onChange={setField('protocol')}>
                <option value="rtsp">rtsp</option>
                <option value="rtsps">rtsps</option>
              </select>
            </div>
            <div className="lm-field lm-field-grow">
              <label>Camera IP / Host</label>
              <input value={form.host} onChange={setField('host')} placeholder="192.168.1.64" required autoComplete="off" />
            </div>
            <div className="lm-field lm-field-sm">
              <label>Port</label>
              <input value={form.port} onChange={setField('port')} placeholder="554" inputMode="numeric" />
            </div>
          </div>
          <div className="lm-row">
            <div className="lm-field lm-field-grow">
              <label>Stream path (optional)</label>
              <input value={form.path} onChange={setField('path')} placeholder="/cam/realmonitor?channel=1&subtype=1" autoComplete="off" />
            </div>
            <div className="lm-field lm-field-sm">
              <label>Transport</label>
              <select value={form.transport} onChange={setField('transport')}>
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
              </select>
            </div>
          </div>
          <div className="lm-row">
            <div className="lm-field lm-field-grow">
              <label>Username</label>
              <input value={form.username} onChange={setField('username')} autoComplete="off" />
            </div>
            <div className="lm-field lm-field-grow">
              <label>Password</label>
              <div className="lm-pw-wrap">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={setField('password')}
                  autoComplete="new-password"
                />
                <button type="button" className="lm-pw-eye" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                  {showPw ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          </div>
          <label className="lm-checkbox">
            <input
              type="checkbox"
              checked={form.remember}
              disabled={!CAN_REMEMBER}
              onChange={e => setForm(f => ({ ...f, remember: e.target.checked }))}
            />
            {CAN_REMEMBER
              ? 'Remember these credentials on this device (encrypted)'
              : 'Remembering credentials needs a secure connection (HTTPS) — not available here'}
          </label>

          {error && <div className="lm-error">⚠️ {error}</div>}

          <button className="lm-btn" type="submit">Add Camera</button>
        </form>
      </div>

      <div className="lm-panel">
        <div className="lm-panel-header">
          <h2>Saved cameras</h2>
          {cameras.length > 0 && (
            <button className="lm-link-btn" onClick={handleClearAll}>Clear all local data</button>
          )}
        </div>
        {cameras.length === 0 ? (
          <p className="lm-empty">No cameras added yet.</p>
        ) : (
          <ul className="lm-cam-list">
            {cameras.map(cam => (
              <li key={cam.id} className="lm-cam-row">
                <div className="lm-cam-info">
                  <span className="lm-cam-name">{cam.name}</span>
                  <span className="lm-cam-sub">
                    {cam.protocol}://{cam.host}:{cam.port || 554}{cam.path} · {(cam.transport || 'tcp').toUpperCase()}
                    {cam.source === 'server-sync' && ' · from account'}
                  </span>
                </div>

                {promptFor === cam.id ? (
                  <InlineCredentialForm
                    onCancel={() => setPromptFor(null)}
                    onConnect={(u, p) => handleInlineConnect(cam, u, p)}
                  />
                ) : (
                  <div className="lm-cam-actions">
                    <button className="lm-btn lm-btn-sm" disabled={!NATIVE_PLAYER} onClick={() => handleView(cam)}>
                      View
                    </button>
                    <button className="lm-link-btn" onClick={() => handleForget(cam.id)}>Forget</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function InlineCredentialForm({ onConnect, onCancel }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  return (
    <form
      className="lm-inline-cred"
      onSubmit={e => { e.preventDefault(); onConnect(username, password); }}
    >
      <input placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" />
      <input placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
      <button className="lm-btn lm-btn-sm" type="submit">Connect</button>
      <button type="button" className="lm-link-btn" onClick={onCancel}>Cancel</button>
    </form>
  );
}
